// Inventory API client — talks to the warehouse backend's Base44-proxying
// endpoints. The backend team shipped the following (2026-09):
//
//   • GET  /api/inventory/locations           → list_locations
//   • GET  /api/inventory/location/{code}     → location_lookup
//
// Auth + X-CD-Mode headers are attached automatically by the shared axios
// instance. All responses are cached in memory for the session so opening
// the location picker doesn't re-fetch 90 rows on every mount.

import api from "@/lib/apiClient";

let _locationsCache = null; // { fetchedAt: Date, locations: [...] }

/**
 * Return the cached list of active locations. Passes `{ force: true }` to
 * bypass the cache (the picker's "Refresh" button uses this).
 * Filters out `is_active: false` and sorts by zone → location_code so the
 * picker is stable across sessions.
 */
export async function listLocations({ force = false } = {}) {
  if (!force && _locationsCache) return _locationsCache;
  const res = await api.get("/inventory/locations");
  const raw = Array.isArray(res.data?.locations) ? res.data.locations : [];
  const locations = raw
    .filter((l) => l && l.is_active !== false)
    .sort((a, b) => {
      const zA = a.zone || "";
      const zB = b.zone || "";
      const zoneCmp = zA.localeCompare(zB, undefined, { sensitivity: "base" });
      if (zoneCmp !== 0) return zoneCmp;
      // Natural / numeric-aware compare so RACK4 sorts before RACK30, and
      // RACK1-SHELF2 sorts before RACK1-SHELF10 (mixed digit widths).
      return (a.location_code || "").localeCompare(
        b.location_code || "",
        undefined,
        { numeric: true, sensitivity: "base" }
      );
    });
  _locationsCache = {
    fetchedAt: new Date(),
    locations,
    total: res.data?.location_count ?? locations.length,
  };
  return _locationsCache;
}

/** Fetch what's on hand at a single location. Not cached (stock changes). */
export async function locationLookup(locationCode) {
  if (!locationCode) throw new Error("locationCode required");
  const res = await api.get(`/inventory/location/${encodeURIComponent(locationCode)}`);
  return res.data;
}

/** Fetch product metadata + aggregate stock. Returns `{ success, sku, name,
 *  category, style, finish, stock_quantity, ... }` or throws 404. */
export async function productLookup(sku) {
  if (!sku) throw new Error("sku required");
  const res = await api.post("/inventory/lookup", { sku });
  return res.data;
}

/**
 * Find every location that has non-zero on-hand quantity for `sku`.
 *
 * Backend has no direct "sku → locations" endpoint, so we fan out. To keep
 * this fast even at 100+ bins, we prune against the cached locations list
 * first: any location with `current_quantity <= 0` in list_locations is
 * empty by definition and safe to skip. That typically leaves a small
 * handful of bins to actually query.
 *
 * Returns `[{ location, quantity }]` sorted by descending quantity.
 * The caller supplies `onProgress` to render a "N of M bins scanned" UX.
 */
export async function findSkuLocations(sku, { onProgress } = {}) {
  if (!sku) throw new Error("sku required");
  const target = String(sku).trim().toUpperCase();

  const cache = await listLocations();
  // Only fan out to bins with any stock at all.
  const candidates = cache.locations.filter(
    (l) => Number(l.current_quantity) > 0
  );

  const total = candidates.length;
  const results = [];
  let done = 0;
  // Cap concurrency so we don't spike the proxy — 6 in-flight is plenty
  // for 100 bins and still finishes in <2s.
  const CONCURRENCY = 6;

  async function worker(iter) {
    for (const loc of iter) {
      try {
        const data = await locationLookup(loc.location_code);
        const items = Array.isArray(data?.items) ? data.items : [];
        const match = items.find(
          (i) => String(i.product_sku || "").toUpperCase() === target
        );
        if (match && Number(match.quantity) > 0) {
          results.push({ location: loc, quantity: Number(match.quantity) });
        }
      } catch (err) {
        // Single-bin failure shouldn't kill the whole search.
      } finally {
        done += 1;
        onProgress?.({ done, total });
      }
    }
  }
  // Slice candidates into round-robin buckets for the workers.
  const buckets = Array.from({ length: CONCURRENCY }, () => []);
  candidates.forEach((c, i) => buckets[i % CONCURRENCY].push(c));
  await Promise.all(buckets.map((b) => worker(b)));

  results.sort((a, b) => b.quantity - a.quantity);
  return { results, scanned: total, total: cache.locations.length };
}

/** Case-insensitive filter across code, name, and zone (matches spec). */
export function filterLocations(locations, query) {
  const q = (query || "").trim().toLowerCase();
  if (!q) return locations;
  return locations.filter((l) => {
    return (
      (l.location_code || "").toLowerCase().includes(q) ||
      (l.location_name || "").toLowerCase().includes(q) ||
      (l.zone || "").toLowerCase().includes(q)
    );
  });
}

/**
 * Group locations by zone for the picker's zone-headed sections.
 * Returns [{ zone: "main_warehouse", locations: [...] }, ...] in the same
 * stable order as the flat sorted list.
 */
export function groupByZone(locations) {
  const groups = new Map();
  for (const l of locations) {
    const z = l.zone || "unassigned";
    if (!groups.has(z)) groups.set(z, []);
    groups.get(z).push(l);
  }
  return Array.from(groups, ([zone, locs]) => ({ zone, locations: locs }));
}

/**
 * Capacity utilization → { pct, tone } where tone is one of green/amber/red
 * matching the spec's 0–70 / 70–89 / 90+ thresholds.
 * Returns `{ pct: null, tone: "muted" }` when capacity is missing or 0.
 */
export function capacityStatus(location) {
  if (!location) return { pct: null, tone: "muted" };
  const cap = Number(location.capacity);
  const cur = Number(location.current_quantity);
  if (!Number.isFinite(cap) || cap <= 0) return { pct: null, tone: "muted" };
  const clean = Number.isFinite(cur) && cur >= 0 ? cur : 0;
  const pct = Math.max(0, Math.min(200, (clean / cap) * 100));
  const tone = pct >= 90 ? "red" : pct >= 70 ? "amber" : "green";
  return { pct, tone, current: clean, capacity: cap };
}

/** Clear the module-level cache (used by the picker's refresh button). */
export function clearLocationsCache() {
  _locationsCache = null;
}

// ---------------------------------------------------------------------------
// Eagle Frames & Doors — Base44 tracks these as two separate counts per SKU.
// The lookup response declares `inventory_type: "components"` and returns
// `eagle_frame_stock_quantity`, `eagle_door_stock_quantity`, and
// `available_complete_sets` (the smaller of the two).
//
// All Receive / Pick / Adjust calls for Eagle SKUs REQUIRE a `component`
// field: "frame" | "door" | "both". Missing component → 400 from Base44.
// Regular (non-Eagle) products must OMIT `component`.
// ---------------------------------------------------------------------------

/** True when a lookup response represents an Eagle (frames + doors) SKU. */
export function isEagleInventory(product) {
  return !!product && product.inventory_type === "components";
}

/**
 * Eagle variation lookups return a different shape than regular products:
 *   • `variation_name` / `parent_product_name` instead of flat `name`
 *   • `attributes: { style, finish, width, height, thickness }` instead of
 *     flat `style` / `finish`
 * Expose a single normalizer so the UI never has to branch inline.
 */
export function normalizeProductMeta(product) {
  if (!product) return { name: "", style: "", finish: "", width: null, height: null, thickness: null };
  const attrs = product.attributes || {};
  return {
    name: product.variation_name || product.parent_product_name || product.name || "",
    style: attrs.style || product.style || "",
    finish: attrs.finish || product.finish || "",
    width: attrs.width ?? product.width ?? null,
    height: attrs.height ?? product.height ?? null,
    thickness: attrs.thickness ?? product.thickness ?? null,
  };
}

/** Low-stock heuristic that honors Eagle's "available_complete_sets" rule. */
export function stockHealth(product) {
  if (!product || product.track_inventory === false) return { tone: "muted", value: null, label: "Not tracked" };
  const low = Number(product.low_stock_threshold) || 0;
  const value = isEagleInventory(product)
    ? Number(product.available_complete_sets) || 0
    : Number(product.stock_quantity) || 0;
  if (value <= 0) return { tone: "red", value, label: "Out of stock" };
  if (low > 0 && value <= low) return { tone: "amber", value, label: `Low (≤ ${low})` };
  return { tone: "green", value, label: "In stock" };
}

/**
 * Receive / Pick / Adjust stock. Shared body shape:
 *   • Regular SKU:  { action, sku, quantity | new_quantity }
 *   • Eagle   SKU:  { action, sku, component, quantity | new_quantity }
 *
 * `component` must be one of "frame" | "door" | "both" for Eagle, and must
 * be omitted entirely for regular products. The caller is responsible for
 * knowing which case applies (via `isEagleInventory` from a prior lookup).
 *
 * Returns the raw response body. Callers typically re-run `productLookup`
 * after a mutation so the UI refreshes from the authoritative counts
 * (there is no `new_stock` field on Eagle responses).
 */
async function mutateStock(action, { sku, component, quantity, new_quantity }) {
  if (!sku) throw new Error("sku required");
  const body = { action, sku };
  if (component) body.component = component;
  if (action === "adjust") {
    if (new_quantity == null) throw new Error("new_quantity required for adjust");
    body.new_quantity = Number(new_quantity);
  } else {
    if (quantity == null) throw new Error("quantity required");
    body.quantity = Number(quantity);
  }
  const res = await api.post(`/inventory/${action}`, body);
  return res.data;
}

export function receiveStock(args) { return mutateStock("receive", args); }
export function pickStock(args)    { return mutateStock("pick", args); }
export function adjustStock(args)  { return mutateStock("adjust", args); }
